import { useCallback } from "react";
import { useApi } from "../../hooks/useApi";
import { useSession } from "../../context/SessionContext";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { SUCCESS_MESSAGES } from "../../i18n/success.pt-BR";

type OcrData = {
  nome?: string;
  data_de_nascimento?: string;
  validade_da_carteirinha?: string;
  cpf?: string;
  numero_carteirinha?: string;
  acomodacao?: string;
  sexo?: string;
};

// O OCR devolve datas em dd/mm/aaaa; os inputs de data esperam aaaa-mm-dd.
function toIsoDate(date?: string): string {
  if (!date) return "";
  const [day, month, year] = date.split("/");
  return `${year}-${month}-${day}`;
}

/**
 * Envia o documento do paciente para o bucket (pasta OCR) e preenche os dados
 * do paciente no OrderDraftContext com o resultado de `POST /dataFromOCR`.
 * Erros aparecem como toast pelo `request` do useApi; as funções resolvem `null` nesse caso.
 */
export function usePatientOcr() {
  const { request, apiFetch, apiFetchForm, loading } = useApi();
  const { metadata } = useSession();
  const { setSurgeryRequestData, setBirthDate, setCpf, setDocumentNumber, setConvenio, setSexo, setDocumentValidity } = useOrderDraft();
  const email = metadata.email;

  const upload = useCallback((file: Blob, fileName: string) => request(() => {
    const formData = new FormData();
    if (email) formData.append("email", email);
    formData.append("folder", "OCR");
    formData.append("template_file", file, fileName);
    return apiFetchForm("/uploadFileToBucket", formData);
  }), [request, apiFetchForm, email]);

  const analyze = useCallback((fileName: string) => request(async () => {
    const res = await apiFetch<{ data: OcrData }>("/dataFromOCR", {
      method: "POST",
      body: JSON.stringify({ email, fileName }),
    });
    const info = res.data;
    setSurgeryRequestData((prev) => ({ ...prev, patientName: info.nome || "" }));
    setBirthDate(toIsoDate(info.data_de_nascimento));
    setCpf(info.cpf || "");
    setDocumentNumber(info.numero_carteirinha || "");
    setConvenio(info.acomodacao || "");
    setDocumentValidity(toIsoDate(info.validade_da_carteirinha));
    setSexo(info.sexo || "");
    return info;
  }, { successMessage: SUCCESS_MESSAGES.documentRead }), [
    request, apiFetch, email, setSurgeryRequestData, setBirthDate, setCpf,
    setDocumentNumber, setConvenio, setDocumentValidity, setSexo,
  ]);

  return { upload, analyze, loading };
}
